import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Trash2, Play, Loader2 } from 'lucide-react';
import { useTheme } from 'shared/theme/ThemeContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { NODE_CATALOG, nodeIssues } from 'features/agents/WorkflowEditor/nodeCatalog';
import NodeMark from 'features/agents/WorkflowEditor/NodeMark';
import { getUpstreamNodes, nodeExprBase } from 'features/agents/WorkflowEditor/workflowGraph';
import { NodeConfigFields, InputPane, IOPane } from 'features/agents/WorkflowEditor/NodeConfigFields';
import { shortName } from 'features/agents/WorkflowEditor/NodePicker';
import { execStatusColor, execStatusLabel, formatExecDuration } from 'features/agents/lib/status';
import { cx, neutralBtnCls } from 'features/agents/WorkflowEditor/fields/inputs';
import { testStep } from 'features/agents/api/agentsApi';

/**
 * A node's settings, in a panel beside the canvas.
 *
 * The canvas shrinks to make room rather than being covered, so the step
 * being edited stays visible in the graph while it is edited.
 *
 * Header: the node's tile, its name (editable in place), what kind of step it
 * is, and, when it is not ready to run, how many things are missing. Settings,
 * Input and Output start as tabs. As THIS sidebar (not the window) is dragged
 * wider, Input becomes a second column beside Settings, and wider still Output
 * a third. Below 640px the panel is the whole screen.
 *
 * `node` is a v2 node: { id, kind, name, config }.
 */

const MIN_W = 360;
const MAX_W = 2000;
const DEFAULT_W = 400;
const MIN_CANVAS_W = 200;
const WIDTH_KEY = 'trodo.nodeConfigPanel.width';
// Staged layout vs the sidebar's own width.
const INPUT_COL_BREAKPOINT = 760;   // tabs -> Settings | Input
const OUTPUT_COL_BREAKPOINT = 1100; // Settings | Input -> + Output
const PHONE_QUERY = '(max-width: 639px)';

const CATEGORY_LABEL = { trigger: 'Trigger', ai: 'AI', data: 'Data', flow: 'Flow', integration: 'App' };
const BRAND = { slack: 'Slack', github: 'GitHub', linear: 'Linear' };

function readWidth() {
  try {
    const raw = Number(localStorage.getItem(WIDTH_KEY));
    if (Number.isFinite(raw) && raw >= MIN_W && raw <= MAX_W) return raw;
  } catch (_) { /* private mode */ }
  return DEFAULT_W;
}

function maxSidebarWidth(el) {
  if (!el) return MAX_W;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0) return MAX_W;
  // Walk past shrink-wrapped parents to the row that actually contains the
  // canvas, so the cap is "leave the graph room".
  let container = el.parentElement;
  while (container && container.clientWidth <= el.offsetWidth + 1) container = container.parentElement;
  const left = container ? container.getBoundingClientRect().left : 0;
  return Math.min(MAX_W, Math.max(MIN_W, Math.floor(rect.right - left - MIN_CANVAS_W)));
}

function usePhone() {
  const query = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(PHONE_QUERY) : null);
  const [phone, setPhone] = useState(() => !!query()?.matches);
  useEffect(() => {
    const mq = query();
    if (!mq) return undefined;
    const on = () => setPhone(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  return phone;
}

/** The dialog's confirm, or nothing when the panel is mounted outside the provider. */
function useConfirm() {
  try { return useAppDialog().confirm; } catch (_) { return null; }
}

/** "Slack · Send message", "Flow · If". */
function kindLabel(spec) {
  const provider = spec.provider || spec.triggerProvider;
  if (provider && BRAND[provider]) return `${BRAND[provider]} · ${shortName(spec, provider)}`;
  return `${CATEGORY_LABEL[spec.category] || 'Step'} · ${spec.displayName}`;
}

export default function NodeConfigPanel({
  node, workflow, onChange, onDelete, onClose,
  readOnly = false,
  stepOverride = null,
  liveStep = null,
  onRunTest = null,
  definition = null,
  testData = {},
  problem = null,
}) {
  const spec = NODE_CATALOG[node?.kind];
  const { theme } = useTheme();
  const confirm = useConfirm();
  const phone = usePhone();
  const [tab, setTab] = useState('config');
  const [testing, setTesting] = useState(false);
  const [executingPrevious, setExecutingPrevious] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [width, setWidth] = useState(readWidth);
  const widthRef = useRef(width);
  const asideRef = useRef(null);
  widthRef.current = width;

  const emitChange = readOnly ? (() => {}) : onChange;
  const nodeId = node?.id;
  useEffect(() => { setTab('config'); setTestResult(null); }, [nodeId]);

  const capWidth = useCallback((w) => Math.min(maxSidebarWidth(asideRef.current), Math.max(MIN_W, w)), []);

  useEffect(() => {
    const clamp = () => setWidth((w) => capWidth(w));
    clamp();
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
  }, [capWidth]);

  const startResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = widthRef.current;
    const onMove = (ev) => setWidth(capWidth(startW + (startX - ev.clientX)));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try { localStorage.setItem(WIDTH_KEY, String(widthRef.current)); } catch (_) { /* private mode */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [capWidth]);

  const upstreamNodes = useMemo(
    () => (definition && nodeId ? getUpstreamNodes(definition, nodeId) : []),
    [definition, nodeId],
  );

  // What "Insert variable" offers: each step before this one, by name.
  const variables = useMemo(() => upstreamNodes.map((n) => {
    const name = n.name || NODE_CATALOG[n.kind]?.displayName || 'Step';
    return { label: name, expr: `${nodeExprBase(name)}.json`, hint: 'Output of this step' };
  }), [upstreamNodes]);

  // What is missing, once, for the badge, the field messages and the header line.
  const issues = useMemo(() => (readOnly || !node ? [] : nodeIssues(node)), [node, readOnly]);
  const issuesByField = useMemo(() => {
    const map = {};
    for (const i of issues) if (i.field && !map[i.field]) map[i.field] = i.message;
    return map;
  }, [issues]);
  const wholeNodeIssue = issues.find((i) => !i.field)?.message || null;

  const step = stepOverride ?? liveStep ?? null;

  const handleExecutePrevious = async () => {
    if (!onRunTest) return;
    setExecutingPrevious(true);
    try { await onRunTest(node.id, { inputsOnly: true }); } catch (_) { /* surfaced in the pane */ }
    finally { setExecutingPrevious(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setTab('output');
    try {
      if (onRunTest) {
        const map = await onRunTest(node.id);
        const r = map?.[node.id];
        setTestResult(r
          ? { ok: r.status !== 'failed', output: r.output, error: r.error }
          : { ok: false, error: { message: 'This step was not reached in the run.' } });
      } else if (!workflow?.id) {
        setTestResult({ ok: false, error: { message: 'Save the agent to test its steps.' } });
      } else {
        setTestResult(await testStep(workflow.id, { node_id: node.id, mock_input: null }));
      }
    } catch (e) {
      setTestResult({ ok: false, error: { message: e?.response?.data?.message || e.message } });
    } finally { setTesting(false); }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    if (confirm) {
      const yes = await confirm(`"${node.name || spec.displayName}" and its connections will be removed.`, { title: 'Delete step', confirmLabel: 'Delete', danger: true });
      if (!yes) return;
    }
    onDelete();
  };

  if (!node || !spec) return null;

  const cfg = node.config || {};
  const canTest = !readOnly && spec.category !== 'trigger';
  const updateField = (key, val) => emitChange({ config: { ...cfg, [key]: val } });
  const updateFields = (patch) => emitChange({ config: { ...cfg, ...patch } });
  const outputValue = testResult ? testResult.output : step?.output;
  const outputError = testResult ? testResult.error : step?.error;
  const layout = phone ? 'tabs'
    : width >= OUTPUT_COL_BREAKPOINT ? 'three'
    : width >= INPUT_COL_BREAKPOINT ? 'two'
    : 'tabs';
  // In two-column mode Settings stays on the left; the right pane is Input
  // unless the user (or Test) asked for Output.
  const ioTab = tab === 'output' ? 'output' : 'input';

  const tabButton = (id, label) => {
    const active = layout === 'two' ? ioTab === id : tab === id;
    return (
      <button
        key={id}
        type="button"
        aria-pressed={active}
        onClick={() => setTab(id)}
        className={cx(
          'relative h-9 px-3 text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--state-focus-ring)]',
          active ? 'text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]',
        )}
      >
        {label}
        {active ? <span className="absolute inset-x-2 -bottom-px h-px bg-[color:var(--text-primary)]" /> : null}
      </button>
    );
  };

  const testButton = canTest ? (
    <button type="button" onClick={handleTest} disabled={testing} title="Run this step, and the steps before it" className={neutralBtnCls}>
      {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
      Test
    </button>
  ) : null;

  const tabsBar = (ids) => (
    <div className="flex h-9 shrink-0 items-center justify-between border-b border-[color:var(--border-default)] pl-1 pr-3">
      <div className="flex items-center">{ids.map(([id, label]) => tabButton(id, label))}</div>
      {testButton}
    </div>
  );

  const colCls = (visible, divider) => (
    visible
      ? `flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden contain-paint${divider ? ' border-r border-[color:var(--border-default)]' : ''}`
      : 'hidden'
  );

  const paneClass = (id) => {
    if (layout === 'three') return colCls(true, id !== 'output');
    if (layout === 'two') {
      if (id === 'config') return colCls(true, true);
      if (id === 'input') return colCls(ioTab === 'input', false);
      return colCls(ioTab === 'output', false);
    }
    return tab === id ? 'flex-1 min-h-0 min-w-0 flex flex-col' : 'hidden';
  };

  const iconBtn = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

  return (
    <aside
      ref={asideRef}
      className={cx(
        'flex flex-col border-l border-[color:var(--border-default)] bg-[color:var(--surface-base)]',
        phone ? 'fixed inset-0 z-40 h-full w-full border-l-0' : 'relative h-full shrink-0',
      )}
      style={phone ? undefined : { width }}
      aria-label={`${spec.displayName} settings`}
    >
      {phone ? null : (
        <div
          onMouseDown={startResize}
          title="Drag to resize"
          className="absolute bottom-0 left-0 top-0 z-10 -ml-0.5 w-1 cursor-col-resize transition-colors hover:bg-[color:var(--border-strong)]"
        />
      )}

      <div className="shrink-0 border-b border-[color:var(--border-default)] px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          <NodeMark kind={node.kind} size={28} />
          <div className="min-w-0 flex-1">
            <input
              type="text"
              value={node.name || ''}
              onChange={(e) => emitChange({ name: e.target.value })}
              readOnly={readOnly}
              aria-label="Step name"
              placeholder={spec.displayName}
              className="h-7 w-full min-w-0 rounded-md border border-transparent bg-transparent px-1.5 -ml-1.5 text-[13px] font-semibold text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] hover:border-[color:var(--border-subtle)] focus:border-[color:var(--border-strong)] focus:outline-none read-only:hover:border-transparent"
            />
            <div className="flex min-w-0 items-center gap-2 text-[11px] leading-4 text-[color:var(--text-muted)]">
              <span className="truncate">{kindLabel(spec)}</span>
              {issues.length > 0 ? (
                <span
                  role="status"
                  aria-label={`${issues.length} ${issues.length === 1 ? 'thing' : 'things'} to fix`}
                  className="inline-flex h-4 shrink-0 items-center rounded-full bg-danger-soft px-1.5 text-[11px] font-medium text-danger"
                >
                  {issues.length} to fix
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            {layout === 'three' ? testButton : null}
            {!readOnly ? (
              <button type="button" onClick={handleDelete} title="Delete step" aria-label="Delete step" className={cx(iconBtn, 'hover:!text-danger')}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            ) : null}
            <button type="button" onClick={onClose} title="Close" aria-label="Close" className={iconBtn}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {step?.status ? (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-[color:var(--text-muted)]">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: execStatusColor(step.status) }} aria-hidden />
            <span className="text-[color:var(--text-primary)]">{execStatusLabel(step.status)}</span>
            {typeof step.duration_ms === 'number' ? <span>{`· ${formatExecDuration(step.duration_ms)}`}</span> : null}
            {step.attempt > 1 ? <span>{`· attempt ${step.attempt}`}</span> : null}
          </div>
        ) : null}
        {!step?.status && (wholeNodeIssue || (problem && issues.length === 0)) ? (
          <p role="alert" className="mt-2 text-[11px] leading-snug text-danger">{wholeNodeIssue || problem}</p>
        ) : null}
      </div>

      {layout === 'tabs' ? tabsBar([['config', 'Settings'], ['input', 'Input'], ['output', 'Output']]) : null}

      <div className={cx('min-h-0 min-w-0 flex-1', layout === 'tabs' ? 'flex flex-col' : 'flex flex-row')}>
        <section className={paneClass('config')} aria-label="Settings">
          {layout !== 'tabs' ? (
            <div className="flex h-10 shrink-0 items-center border-b border-[color:var(--border-default)] px-3">
              <span className="text-[12px] font-semibold text-[color:var(--text-primary)]">Settings</span>
            </div>
          ) : null}
          <fieldset
            disabled={readOnly}
            className="min-h-0 w-full min-w-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden px-3 py-4"
            style={{ border: 0, margin: 0, minInlineSize: 0 }}
          >
            <NodeConfigFields
              spec={spec}
              cfg={cfg}
              updateField={updateField}
              updateFields={updateFields}
              theme={theme}
              workflow={workflow}
              readOnly={readOnly}
              issues={issuesByField}
              variables={variables}
            />
          </fieldset>
        </section>

        <div className={layout === 'two' ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden contain-paint' : 'contents'}>
          {layout === 'two' ? tabsBar([['input', 'Input'], ['output', 'Output']]) : null}
          <section className={paneClass('input')} aria-label="Input">
            {readOnly || stepOverride !== null ? (
              <IOPane title="Input" value={step?.input} exprBase="$json" />
            ) : (
              <InputPane upstreamNodes={upstreamNodes} testData={testData} draggable executing={executingPrevious} onExecutePrevious={onRunTest ? handleExecutePrevious : null} />
            )}
          </section>
          <section className={paneClass('output')} aria-label="Output">
            <IOPane title="Output" value={outputValue} error={outputError} emptyHint={readOnly ? undefined : 'Test this step to see what it returns.'} />
          </section>
        </div>
      </div>
    </aside>
  );
}
